import React, { useState, useEffect, useRef } from 'react';
import { sendRequest } from '../../api';

// Add line-clamp utility if not available
const styles = `
  .line-clamp-2 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  @keyframes fade-in-up {
    0% {
      opacity: 0;
      transform: translateY(20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes spin-slow {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  @keyframes pulse-slow {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }

  @keyframes blink {
    0%, 50% {
      opacity: 1;
    }
    51%, 100% {
      opacity: 0;
    }
  }

  .animate-fade-in-up {
    animation: fade-in-up 0.6s ease-out forwards;
  }

  .animate-spin-slow {
    animation: spin-slow 3s linear infinite;
  }

  .animate-shimmer {
    animation: shimmer 2s ease-in-out infinite;
  }

  .animate-pulse-slow {
    animation: pulse-slow 2s ease-in-out infinite;
  }

  .animate-blink {
    animation: blink 1s ease-in-out infinite;
  }

  .delay-100 {
    animation-delay: 0.1s;
  }

  .delay-200 {
    animation-delay: 0.2s;
  }

  .delay-300 {
    animation-delay: 0.3s;
  }

  .delay-500 {
    animation-delay: 0.5s;
  }

  .delay-700 {
    animation-delay: 0.7s;
  }

  .bg-grid-gray-100 {
    background-image: 
      linear-gradient(rgba(156, 163, 175, 0.1) 1px, transparent 1px),
      linear-gradient(90deg, rgba(156, 163, 175, 0.1) 1px, transparent 1px);
    background-size: 20px 20px;
  }

  .bg-grid-gray-800 {
    background-image: 
      linear-gradient(rgba(75, 85, 99, 0.2) 1px, transparent 1px),
      linear-gradient(90deg, rgba(75, 85, 99, 0.2) 1px, transparent 1px);
    background-size: 20px 20px;
  }
`;

// Utility function to extract URLs from text
const extractUrls = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.match(urlRegex) || [];
};

// Extract domain and get favicon
const getDomainInfo = (url) => {
  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname.replace('www.', '');
    const favicon = `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
    return { domain, favicon };
  } catch (e) {
    return { domain: 'Unknown', favicon: null };
  }
};

// Rich Link Preview Component (simplified)
const LinkPreview = ({ url, title, summary, className = "" }) => {
  const { domain, favicon } = getDomainInfo(url);
  
  return (
    <a 
      href={url} 
      target="_blank" 
      rel="noopener noreferrer"
      className={`block border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-500 group transform hover:-translate-y-0.5 ${className}`}
    >
      <div className="flex gap-3">
        <div className="flex-shrink-0">
          {favicon && (
            <img 
              src={favicon} 
              alt={domain}
              className="w-8 h-8 rounded"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          )}
          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded flex items-center justify-center text-blue-600 dark:text-blue-400 text-sm font-medium" style={{display: 'none'}}>
            🔗
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h5 className="font-medium text-gray-900 dark:text-white text-sm leading-tight mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {title}
          </h5>
          {summary && (
            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
              {summary}
            </p>
          )}
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500">
            <span className="text-blue-600 dark:text-blue-400 font-medium">{domain}</span>
            <span>•</span>
            <span className="group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors flex items-center gap-1">
              Read article
              <svg className="w-3 h-3 transform group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </span>
          </div>
        </div>
      </div>
    </a>
  );
};

// Enhanced Clickable Text Component
const ClickableText = ({ children, className = "" }) => {
  if (typeof children !== 'string') return children;

  const urls = extractUrls(children);
  if (urls.length === 0) return <span className={className}>{children}</span>;

  let lastIndex = 0;
  const parts = [];

  urls.forEach((url, i) => {
    const urlIndex = children.indexOf(url, lastIndex);
    
    // Add text before URL
    if (urlIndex > lastIndex) {
      parts.push(children.slice(lastIndex, urlIndex));
    }
    
    const { domain, favicon } = getDomainInfo(url);
    
    // Add enhanced clickable URL
    parts.push(
      <a
        key={i}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-all"
        title={`Visit ${domain}`}
      >
        {favicon && (
          <img 
            src={favicon} 
            alt={domain}
            className="w-3 h-3 flex-shrink-0"
            onError={(e) => e.target.style.display = 'none'}
          />
        )}
        <span className="truncate max-w-xs">{domain}</span>
        <svg className="w-3 h-3 flex-shrink-0 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </a>
    );
    
    lastIndex = urlIndex + url.length;
  });

  // Add remaining text
  if (lastIndex < children.length) {
    parts.push(children.slice(lastIndex));
  }

  return <span className={className}>{parts}</span>;
};

const AgentActivityFeed = ({ topicId, topicName }) => {
  const [activities, setActivities] = useState([]);
  const [waitingForHuman, setWaitingForHuman] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState(null);
  const [userResponse, setUserResponse] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lastActivityId, setLastActivityId] = useState(null);
  const [newActivitiesCount, setNewActivitiesCount] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const feedEndRef = useRef(null);

  useEffect(() => {
    fetchActivities();
    // Poll for updates every 3 seconds
    const interval = setInterval(fetchActivities, 3000);
    return () => clearInterval(interval);
  }, [topicId]);

  // Streaming text effect for topic name when agent is starting
  useEffect(() => {
    if (activities.length === 0 && !loading && topicName) {
      setIsStreaming(true);
      setStreamingText('');
      
      let currentIndex = 0;
      const streamInterval = setInterval(() => {
        if (currentIndex < topicName.length) {
          setStreamingText(topicName.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          // Restart the animation after a pause
          setTimeout(() => {
            setStreamingText('');
            currentIndex = 0;
          }, 2000);
        }
      }, 50); // 50ms per character for smooth streaming

      return () => {
        clearInterval(streamInterval);
        setIsStreaming(false);
      };
    } else {
      setIsStreaming(false);
      setStreamingText(topicName || '');
    }
  }, [activities.length, loading, topicName]);

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setScrollPosition(scrollTop);
      // Hide new activities indicator when user scrolls to top
      if (scrollTop < 100) {
        setNewActivitiesCount(0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle new activities without disrupting scroll position
  useEffect(() => {
    if (activities.length > 0) {
      const latestActivity = activities[0]; // First item after sorting by date DESC
      if (latestActivity && latestActivity.id !== lastActivityId) {
        if (lastActivityId !== null) {
          // Count new activities since last check
          const newCount = activities.findIndex(activity => activity.id === lastActivityId);
          if (newCount > 0 && scrollPosition > 100) {
            setNewActivitiesCount(prev => prev + newCount);
          }
        }
        setLastActivityId(latestActivity.id);
      }
    }
  }, [activities, lastActivityId, scrollPosition]);

  const fetchActivities = async () => {
    try {
      const response = await sendRequest(`/topics/${topicId}/agent/activities`, 'GET');
      const rawActivities = response.activities || [];
      
      // Sort activities by timestamp in descending order (newest first)
      const sortedActivities = rawActivities.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB - dateA; // Newest first
      });
      
      setActivities(sortedActivities);
      setWaitingForHuman(response.waiting_for_human || false);
      setPendingQuestion(response.pending_question || null);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching activities:', error);
      setLoading(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setNewActivitiesCount(0);
  };

  const submitResponse = async () => {
    if (!userResponse.trim()) return;

    setSubmitting(true);
    try {
      await sendRequest(`/topics/${topicId}/agent/respond`, 'POST', {
        question_id: pendingQuestion?.id,
        response: userResponse
      });
      setUserResponse('');
      setPendingQuestion(null);
      setWaitingForHuman(false);
      
      // Refresh activities after a short delay
      setTimeout(fetchActivities, 1000);
    } catch (error) {
      console.error('Error submitting response:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderActivity = (activity) => {
    const { activity_type, activity_data, created_at } = activity;
    const timestamp = new Date(created_at).toLocaleString();

    switch (activity_type) {
      case 'discovery_cycle':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border-l-4 border-indigo-500 dark:border-purple-500 hover:shadow-2xl transition-all duration-300">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <strong className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-3">
                  <span className="text-2xl">🔍</span>
                  <div className="flex flex-col">
                    <span className="text-lg leading-tight">
                      {activity_data.exploration_question || `Discovery Cycle #${activity_data.cycle_number}`}
                    </span>
                    {activity_data.exploration_question && (
                      <span className="text-sm text-gray-500 dark:text-gray-400 font-normal mt-1">
                        Cycle #{activity_data.cycle_number} • {activity_data.exploration_type?.replace('_', ' ') || 'exploration'}
                      </span>
                    )}
                  </div>
                </strong>
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{timestamp}</span>
              </div>
              
              {/* Summary Stats */}
              <div className="flex flex-wrap gap-3 mb-5">
                <span className="px-4 py-2 bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 rounded-full text-sm font-medium border border-indigo-200 dark:border-indigo-700">
                  {activity_data.total_content_found} items found
                </span>
                <span className="px-4 py-2 bg-green-100 dark:bg-green-900/60 text-green-700 dark:text-green-300 rounded-full text-sm font-medium border border-green-200 dark:border-green-700">
                  {activity_data.valuable_discoveries} valuable
                </span>
                <span className="px-4 py-2 bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium border border-blue-200 dark:border-blue-700">
                  {activity_data.sources_searched} sources
                </span>
                {activity_data.duration_seconds && (
                  <span className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm border border-gray-200 dark:border-gray-600">
                    {Math.round(activity_data.duration_seconds)}s
                  </span>
                )}
              </div>

              {/* Search Queries Used */}
              {activity_data.search_queries && activity_data.search_queries.length > 0 && (
                <div className="mb-5">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Search Queries:</h4>
                  <div className="flex flex-wrap gap-2">
                    {activity_data.search_queries.map((query, idx) => (
                      <span key={idx} className="px-3 py-1 bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded border border-gray-200 dark:border-gray-600 text-xs">
                        {query}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Source Breakdown */}
              {activity_data.source_breakdown && (
                <div className="mb-5">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Sources Explored:</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(activity_data.source_breakdown).map(([source, count]) => (
                      <span key={source} className="px-3 py-1 bg-purple-50 dark:bg-purple-900/60 text-purple-700 dark:text-purple-300 rounded border border-purple-200 dark:border-purple-700 text-xs">
                        {source}: {count}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Discovered Content */}
              {activity_data.discovered_items && activity_data.discovered_items.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Content Discovered:</h4>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {activity_data.discovered_items
                      .sort((a, b) => (b.relevance_score || 0) - (a.relevance_score || 0)) // Sort by relevance descending
                      .map((item, idx) => (
                      <div key={idx} className="space-y-3">
                        <LinkPreview 
                          url={item.url}
                          title={item.title}
                          summary={item.summary}
                          className="hover:shadow-lg transition-shadow"
                        />
                        <div className="flex items-center gap-2 px-3">
                          <span className="text-xs px-2 py-1 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded border border-gray-300 dark:border-gray-600">
                            {item.source}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded border ${
                            item.relevance_score >= 0.7 
                              ? 'bg-green-100 dark:bg-green-900/60 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700'
                              : item.relevance_score >= 0.4
                              ? 'bg-yellow-100 dark:bg-yellow-900/60 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700' 
                              : 'bg-red-100 dark:bg-red-900/60 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700'
                          }`}>
                            {Math.round(item.relevance_score * 100)}% relevant
                          </span>
                          {item.author && (
                            <span className="text-xs text-gray-500 dark:text-gray-500">
                              by {item.author}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Unexpected Findings */}
              {activity_data.unexpected_findings && activity_data.unexpected_findings.length > 0 && (
                <div className="mt-5 p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-3">
                    🤔 Unexpected Findings:
                  </h4>
                  <div className="space-y-2">
                    {activity_data.unexpected_findings.map((finding, idx) => (
                      <div key={idx} className="text-xs text-yellow-700 dark:text-yellow-300">
                        <strong><ClickableText>{finding.title}</ClickableText></strong>
                        {finding.why_unexpected && (
                          <p className="mt-1 text-yellow-600 dark:text-yellow-400">
                            <ClickableText>{finding.why_unexpected}</ClickableText>
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'read_content':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border-l-4 border-blue-500 hover:shadow-md transition-shadow">
            <div className="p-5">
              <div className="flex justify-between items-center mb-4">
                <strong className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  📖 Read Article
                </strong>
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{timestamp}</span>
              </div>
              
              <LinkPreview 
                url={activity_data.url}
                title={activity_data.title}
                summary={activity_data.summary}
                className="mb-4"
              />
              
              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded font-medium">
                  {activity_data.source}
                </span>
                <span>Relevance: {(activity_data.relevance_score * 100).toFixed(0)}%</span>
              </div>
            </div>
          </div>
        );

      case 'form_hypothesis':
        return (
          <div className="flex gap-4 p-5 bg-amber-50 dark:bg-amber-900/10 rounded-lg shadow-sm border-l-4 border-amber-500 hover:shadow-md transition-shadow">
            <div className="text-3xl">💡</div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-2">
                <strong className="text-sm font-semibold text-gray-700 dark:text-gray-300">Formed Hypothesis</strong>
                <span className="text-xs text-gray-500 dark:text-gray-400">{timestamp}</span>
              </div>
              <p className="text-base text-gray-900 dark:text-white mb-4 italic">
                <ClickableText>{activity_data.hypothesis_text}</ClickableText>
              </p>
              <div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Confidence: {(activity_data.confidence_score * 100).toFixed(0)}%
                </div>
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-300"
                    style={{ width: `${activity_data.confidence_score * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 'update_memory':
        return (
          <div className="flex gap-4 p-5 bg-purple-50 dark:bg-purple-900/10 rounded-lg shadow-sm border-l-4 border-purple-500 hover:shadow-md transition-shadow">
            <div className="text-3xl">🧠</div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-2">
                <strong className="text-sm font-semibold text-gray-700 dark:text-gray-300">Updated Memory</strong>
                <span className="text-xs text-gray-500 dark:text-gray-400">{timestamp}</span>
              </div>
              <p className="text-sm text-gray-900 dark:text-white mb-3">
                <ClickableText>{activity_data.memory_content}</ClickableText>
              </p>
              <div className="flex items-center gap-3 text-xs">
                <span className={`px-2 py-1 rounded font-medium capitalize ${
                  activity_data.memory_type === 'episodic' 
                    ? 'bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-200'
                    : 'bg-violet-200 dark:bg-violet-800 text-violet-700 dark:text-violet-200'
                }`}>
                  {activity_data.memory_type}
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  Importance: {(activity_data.importance_score * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          </div>
        );

      case 'generate_curiosity':
        return (
          <div className="flex gap-4 p-5 bg-green-50 dark:bg-green-900/10 rounded-lg shadow-sm border-l-4 border-green-500 hover:shadow-md transition-shadow">
            <div className="text-3xl">🤔</div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-2">
                <strong className="text-sm font-semibold text-gray-700 dark:text-gray-300">New Curiosity</strong>
                <span className="text-xs text-gray-500 dark:text-gray-400">{timestamp}</span>
              </div>
              <p className="text-base text-gray-900 dark:text-white font-medium mb-3">
                <ClickableText>{activity_data.curiosity_content}</ClickableText>
              </p>
              <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                <span className="px-2 py-1 bg-green-200 dark:bg-green-800 text-green-700 dark:text-green-200 rounded font-medium">
                  {activity_data.curiosity_type}
                </span>
                <span>Priority: {(activity_data.priority_score * 100).toFixed(0)}%</span>
              </div>
            </div>
          </div>
        );

      case 'start_exploration':
        return (
          <div className="flex gap-4 p-5 bg-indigo-50 dark:bg-indigo-900/10 rounded-lg shadow-sm border-l-4 border-indigo-500 hover:shadow-md transition-shadow">
            <div className="text-3xl">🔍</div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-2">
                <strong className="text-sm font-semibold text-gray-700 dark:text-gray-300">Started Exploration</strong>
                <span className="text-xs text-gray-500 dark:text-gray-400">{timestamp}</span>
              </div>
              <p className="text-sm text-gray-900 dark:text-white">
                Searching for: {activity_data.search_queries?.join(', ')}
              </p>
              <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                {activity_data.exploration_type}
              </div>
            </div>
          </div>
        );

      case 'complete_exploration':
        return (
          <div className="flex gap-4 p-5 bg-emerald-50 dark:bg-emerald-900/10 rounded-lg shadow-sm border-l-4 border-emerald-500 hover:shadow-md transition-shadow">
            <div className="text-3xl">✅</div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-2">
                <strong className="text-sm font-semibold text-gray-700 dark:text-gray-300">Completed Exploration</strong>
                <span className="text-xs text-gray-500 dark:text-gray-400">{timestamp}</span>
              </div>
              <div className="flex flex-wrap gap-3 text-sm text-gray-600 dark:text-gray-400">
                <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
                  Found {activity_data.content_discovered} items
                </span>
                <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
                  {activity_data.valuable_discoveries} valuable
                </span>
                <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
                  {activity_data.new_curiosities} new questions
                </span>
              </div>
              {activity_data.duration_seconds && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Duration: {Math.round(activity_data.duration_seconds)}s
                </div>
              )}
            </div>
          </div>
        );

      case 'ask_human':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border-l-4 border-red-500 hover:shadow-md transition-shadow">
            <div className="p-5">
              <div className="flex justify-between items-center mb-3">
                <strong className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  ❓ Agent Question
                </strong>
                <span className="text-xs text-gray-500 dark:text-gray-400">{timestamp}</span>
              </div>
              
              <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-base text-gray-900 dark:text-white font-medium leading-relaxed">
                  <ClickableText>{activity_data.question_text}</ClickableText>
                </p>
              </div>
              
              {/* Question Context */}
              {activity_data.question_context && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Context:</h4>
                  <div className="flex flex-wrap gap-2">
                    {activity_data.question_context.reason && (
                      <span className="px-2 py-1 bg-orange-50 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 rounded text-xs">
                        Reason: {activity_data.question_context.reason}
                      </span>
                    )}
                    {activity_data.question_context.recent_discoveries && (
                      <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-xs">
                        Recent discoveries: {activity_data.question_context.recent_discoveries}
                      </span>
                    )}
                    {activity_data.question_context.hypotheses_count && (
                      <span className="px-2 py-1 bg-purple-50 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded text-xs">
                        Hypotheses: {activity_data.question_context.hypotheses_count}
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Question Type */}
              {activity_data.question_type && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Question type:</span>
                  <span className="px-3 py-1 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-full text-sm font-medium capitalize">
                    {activity_data.question_type.replace('_', ' ')}
                  </span>
                </div>
              )}
              
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-400 italic">
                  💡 The agent is waiting for your guidance to continue its exploration effectively.
                </p>
              </div>
            </div>
          </div>
        );

      case 'human_response':
        return (
          <div className="flex gap-4 p-5 bg-sky-50 dark:bg-sky-900/10 rounded-lg shadow-sm border-l-4 border-sky-500 hover:shadow-md transition-shadow">
            <div className="text-3xl">💬</div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-2">
                <strong className="text-sm font-semibold text-gray-700 dark:text-gray-300">Your Response</strong>
                <span className="text-xs text-gray-500 dark:text-gray-400">{timestamp}</span>
              </div>
              <p className="text-base text-gray-900 dark:text-white">
                <ClickableText>{activity_data.response}</ClickableText>
              </p>
            </div>
          </div>
        );

      case 'agent_thinking':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border-l-4 border-indigo-400 hover:shadow-md transition-shadow">
            <div className="p-4">
              <div className="flex justify-between items-center mb-3">
                <strong className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  🧠 Agent Thinking
                </strong>
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{timestamp}</span>
              </div>
              
              <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3">
                <p className="text-sm text-indigo-800 dark:text-indigo-200 font-medium">
                  {activity_data.action === 'agent_started' && `🚀 AI Agent started autonomous research for "${activity_data.topic_name}"`}
                  {activity_data.action === 'generating_initial_curiosities' && `Generating initial research questions about "${activity_data.topic_name}"`}
                  {activity_data.action === 'evaluating_curiosities' && `Evaluating ${activity_data.curiosity_count || 0} research questions`}
                  {activity_data.action === 'no_curiosities_found' && 'No active research questions - requesting guidance'}
                  {activity_data.action === 'generated_exploration_strategy' && `Generated exploration strategy: ${activity_data.strategy_focus}`}
                  {activity_data.action === 'fallback_to_basic_exploration' && 'Using fallback exploration strategy'}
                  {activity_data.action === 'processing_discovery_results' && `Processing ${activity_data.content_count} discovery results`}
                  {activity_data.action === 'completed_discovery_analysis' && `Completed analysis - found ${activity_data.valuable_findings} valuable items`}
                  {activity_data.action === 'deciding_next_action' && `Deciding next action (${activity_data.current_curiosities} curiosities available)`}
                </p>
              </div>
            </div>
          </div>
        );

      case 'source_exploration':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border-l-4 border-green-500 hover:shadow-md transition-shadow">
            <div className="p-4">
              <div className="flex justify-between items-center mb-3">
                <strong className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  🔍 Source Exploration
                </strong>
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{timestamp}</span>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900/60 text-green-700 dark:text-green-300 rounded text-xs font-medium">
                    {activity_data.source}
                  </span>
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    {activity_data.query_count} queries
                  </span>
                </div>
                
                {activity_data.queries && activity_data.queries.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded p-2">
                    <div className="flex flex-wrap gap-1">
                      {activity_data.queries.map((query, idx) => (
                        <span key={idx} className="text-xs bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded border">
                          {query}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'update_memory':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border-l-4 border-yellow-500 hover:shadow-md transition-shadow">
            <div className="p-4">
              <div className="flex justify-between items-center mb-3">
                <strong className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  🧠 Memory Update
                </strong>
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{timestamp}</span>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/60 text-yellow-700 dark:text-yellow-300 rounded text-xs font-medium">
                    {activity_data.action || 'stored'}
                  </span>
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs">
                    {activity_data.memory_type || 'episodic'}
                  </span>
                  {activity_data.importance_score && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      importance: {Math.round(activity_data.importance_score * 100)}%
                    </span>
                  )}
                </div>
                
                {activity_data.memory_content && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded p-2">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <ClickableText>{activity_data.memory_content}</ClickableText>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'generate_curiosity':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border-l-4 border-purple-500 hover:shadow-md transition-shadow">
            <div className="p-4">
              <div className="flex justify-between items-center mb-3">
                <strong className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  🤔 New Research Question
                </strong>
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{timestamp}</span>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/60 text-purple-700 dark:text-purple-300 rounded text-xs font-medium">
                    {activity_data.curiosity_type || 'research'}
                  </span>
                  {activity_data.priority_score && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      priority: {Math.round(activity_data.priority_score * 100)}%
                    </span>
                  )}
                </div>
                
                {activity_data.curiosity_content && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded p-3">
                    <p className="text-sm font-medium text-purple-800 dark:text-purple-200">
                      <ClickableText>{activity_data.curiosity_content}</ClickableText>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="flex gap-4 p-5 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-sm border-l-4 border-gray-400 hover:shadow-md transition-shadow">
            <div className="text-3xl">📝</div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-2">
                <strong className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {activity_type.replace(/_/g, ' ')}
                </strong>
                <span className="text-xs text-gray-500 dark:text-gray-400">{timestamp}</span>
              </div>
              <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-x-auto">
                {JSON.stringify(activity_data, null, 2)}
              </pre>
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
        <style>{styles}</style>
        <div className="max-w-4xl mx-auto p-6">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 mb-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold">AI</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Agent Activity Feed</h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Tracking intelligence for{' '}
                  <span className="text-purple-600 dark:text-purple-400 font-medium">
                    {isStreaming ? (
                      <>
                        {streamingText}
                        <span className="animate-blink text-blue-500 ml-0.5">|</span>
                      </>
                    ) : (
                      topicName
                    )}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-center items-center h-64">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-8 border border-gray-200 dark:border-gray-700">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
              <p className="text-gray-600 dark:text-gray-400 mt-4 text-center">Loading agent activities...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <style>{styles}</style>
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 mb-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold">AI</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Agent Activity Feed</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Tracking intelligence for{' '}
                <span className="text-purple-600 dark:text-purple-400 font-medium">
                  {isStreaming ? (
                    <>
                      {streamingText}
                      <span className="animate-blink text-blue-500 ml-0.5">|</span>
                    </>
                  ) : (
                    topicName
                  )}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* New Activities Indicator */}
        {newActivitiesCount > 0 && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50">
            <button
              onClick={scrollToTop}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 transition-all hover:scale-105"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
              {newActivitiesCount} new {newActivitiesCount === 1 ? 'activity' : 'activities'}
            </button>
          </div>
        )}

        {/* Human Question Section */}
        {waitingForHuman && pendingQuestion && (
          <div className="bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/50 dark:to-blue-900/50 backdrop-blur rounded-lg shadow-xl p-6 mb-6 border border-purple-200 dark:border-purple-500/30">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">AI</span>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  🤔 Your Agent Needs Guidance
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">{pendingQuestion.question_text}</p>
                
                <div className="space-y-3">
                  <textarea
                    value={userResponse}
                    onChange={(e) => setUserResponse(e.target.value)}
                    placeholder="Share your thoughts or preferences..."
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    rows="3"
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={submitResponse}
                      disabled={submitting || !userResponse.trim()}
                      className="px-6 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {submitting ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Sending...
                        </span>
                      ) : (
                        'Send Response'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Activities Feed */}
        <div className="space-y-4">
          {activities.length === 0 ? (
            <div className="relative overflow-hidden bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700">
              {/* Animated background pattern */}
              <div className="absolute inset-0 bg-grid-gray-100 dark:bg-grid-gray-800 opacity-30"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-blue-500/5 dark:from-purple-400/10 dark:to-blue-400/10"></div>
              
              {/* Floating orbs animation */}
              <div className="absolute top-4 left-4 w-12 h-12 bg-purple-500/20 rounded-full animate-pulse"></div>
              <div className="absolute top-8 right-8 w-8 h-8 bg-blue-500/20 rounded-full animate-bounce delay-75"></div>
              <div className="absolute bottom-8 left-8 w-6 h-6 bg-indigo-500/20 rounded-full animate-ping delay-150"></div>
              
              {/* Main content */}
              <div className="relative z-10 p-12 text-center">
                {/* Animated avatar */}
                <div className="relative mx-auto mb-8 w-24 h-24">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full animate-spin-slow opacity-20"></div>
                  <div className="absolute inset-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center shadow-2xl">
                    <div className="relative">
                      <span className="text-white font-bold text-2xl">AI</span>
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-ping"></div>
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full"></div>
                    </div>
                  </div>
                </div>

                {/* Animated title */}
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 animate-fade-in-up">
                  Agent is Getting Started
                </h3>
                
                {/* Animated subtitle */}
                <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto animate-fade-in-up delay-100">
                  Your AI agent is initializing its neural networks and will begin discovering content shortly.
                </p>

                {/* Progress indicators */}
                <div className="space-y-4 max-w-sm mx-auto">
                  <div className="flex items-center space-x-3 animate-fade-in-up delay-200">
                    <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-gray-700 dark:text-gray-300">Initializing discovery engines</span>
                  </div>
                  <div className="flex items-center space-x-3 animate-fade-in-up delay-300">
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full animate-pulse delay-75"></div>
                    <span className="text-sm text-gray-700 dark:text-gray-300">Connecting to content sources</span>
                  </div>
                  <div className="flex items-center space-x-3 animate-fade-in-up delay-500">
                    <div className="flex-shrink-0 w-2 h-2 bg-purple-500 rounded-full animate-pulse delay-150"></div>
                    <span className="text-sm text-gray-700 dark:text-gray-300">Calibrating relevance algorithms</span>
                  </div>
                </div>

                {/* Animated progress bar */}
                <div className="mt-8 animate-fade-in-up delay-700">
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full animate-pulse-slow relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 animate-pulse">
                    Preparing for discovery...
                  </p>
                </div>
              </div>
            </div>
          ) : (
            activities.map((activity) => (
              <div key={activity.id} className="animate-in slide-in-from-bottom duration-300">
                {renderActivity(activity)}
              </div>
            ))
          )}
        </div>

        <div ref={feedEndRef} />
      </div>
    </div>
  );
};

export default AgentActivityFeed; 